import '@/features/charts/style.css';
import { memo, useMemo } from 'react';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import ForwardCurveChartBlock from '@/features/markets/components/Chart/ForwardCurveChartBlock';
import { FullScreenChartContent } from '@/features/markets/components/Chart/FullScreenChartContent';
import SeasonalChartBlock from '@/features/markets/components/Chart/SeasonalChartBlock';
import { SpotChartBlock } from '@/features/markets/components/Chart/SpotChartBlock';
import useMarketsChart from '@/features/markets/components/Chart/useMarketsChart';
import { useMarketWidget } from '@/features/markets/components/contexts/MarketWidgetContext';
import { LightModeSwitch } from '@/features/markets/components/LightMode/LightModeSwitch';
import type { ChartsBlockTabType } from '@/features/markets/types';

type ChartsBlockProps<T> = {
  tab: ChartsBlockTabType<T>;
  layout?: { fullWidth?: boolean; height?: number };
};

const ChartsBlock = <T,>({ tab, layout }: ChartsBlockProps<T>) => {
  const { activeTab, templateChartSeries, metadata } = useMarketsChart(tab);
  const isForwardCurve = metadata?.graphType === 'forwardSimple';
  const isSeasonal = metadata?.graphType === 'seasonal';
  const chartDataRange = { from: metadata?.from ?? '5y', to: metadata?.to };
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewChartInFullscreen);
  const activeTabName = useMemo(() => tab.name, [activeTab, tab]);
  const { isFullScreen } = useMarketWidget();
  if (isForwardCurve) {
    return (
      <ForwardCurveChartBlock
        key={`forwardCurveChartBlock${activeTab}`}
        layout={layout}
        templateChartSeries={templateChartSeries}
      />
    );
  } else if (isSeasonal) {
    return (
      <SeasonalChartBlock
        key={`seasonalChartBlock${activeTab}`}
        chartDataRange={chartDataRange}
        layout={layout}
        templateChartSeries={templateChartSeries}
      />
    );
  }

  if (isFullScreen && isPermissionGranted) {
    return (
      <section
        aria-label='Full-screen market chart'
        className='flex flex-col items-stretch'
      >
        <header className='flex justify-end'>
          <LightModeSwitch />
        </header>
        <FullScreenChartContent
          key={activeTab}
          activeTab={activeTab}
          tabName={activeTabName}
          templateChartSeries={templateChartSeries}
        />
      </section>
    );
  }
  return (
    <SpotChartBlock
      key={`spotChartBlock${activeTab}`}
      layout={layout}
      templateChartSeries={templateChartSeries}
    />
  );
};

export default memo(ChartsBlock);
